import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Stack, Typography, useTheme } from '@mui/material';
import { getUnlinkedSaiaLicenses } from 'API/linkedin-accounts';
import { LinkedinTypes } from 'enums';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import type { License } from 'types/licenses.types';
import OptionCard from './option-card';
import StepActions from './step-actions';
import StepLayout from './step-layout';

const PRODUCT_LABEL_KEYS: Record<string, string> = {
  saia_recruiting: 'saiaRecruiting',
  saia_sales: 'saiaSales',
};

interface Props {
  selectedLinkedinProduct: LinkedinTypes;
  selectedLicenseId: string | null;

  handleLinkedinProductChange(value: string): () => void;

  onLicenseSelect(licenseId: string | null): void;

  onNext(): void;

  /** Abbrechen schließt den Assistenten — auf dem ersten Schritt gibt es kein Zurück. */
  onCancel?(): void;
}

/**
 * Schritt 1: Welches LinkedIn-Produkt, und auf welchem Sitz.
 *
 * Beides steht zusammen, weil beides dieselbe Frage beantwortet — was für ein Konto wird
 * hier eingerichtet. Der Sitz steht dabei bewusst vor den Zugangsdaten: er ist das
 * Einzige an diesem Schritt, das fehlschlagen kann.
 */
const SelectProductStep: React.FC<Props> = ({
  selectedLinkedinProduct,
  selectedLicenseId,
  handleLinkedinProductChange,
  onLicenseSelect,
  onNext,
  onCancel,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const [unlinkedLicenses, setUnlinkedLicenses] = useState<License[]>([]);
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFetchLoading(true);
      const res = await getUnlinkedSaiaLicenses();
      if (!cancelled && Array.isArray(res)) {
        setUnlinkedLicenses(res);
      }
      if (!cancelled) setFetchLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasLicenses = unlinkedLicenses.length > 0;

  const linkedinProducts = [
    {
      value: LinkedinTypes.LINKEDIN,
      label: t('appSyncProductLinkedin', 'LinkedIn Standard'),
      subtitle: t('appSyncProductLinkedinDescription', 'Vernetzen, Nachrichten, Postfach'),
      icon: <LinkedInIcon />,
      color: theme.palette.primary.main,
    },
    {
      value: LinkedinTypes.RECRUITER,
      label: t('appSyncProductRecruiter', 'Recruiter'),
      subtitle: t('appSyncProductRecruiterDescription', 'Talentsuche, InMail, Projekte'),
      icon: <PersonSearchIcon />,
      color: theme.palette.recruiting.main,
    },
    {
      value: LinkedinTypes.SALES_NAV,
      label: t('appSyncProductSalesNavigator', 'Sales Navigator'),
      subtitle: t('appSyncProductSalesNavigatorDescription', 'Lead-Suche, Listen, InMail'),
      icon: <TrendingUpIcon />,
      color: theme.palette.sales.main,
    },
  ];

  const createLicenseLabel = (license: License) => {
    const product = t(PRODUCT_LABEL_KEYS[license.product] ?? license.product);
    const billing = t(`billingType_${license.billingType}`, license.billingType);
    return `${product} · ${billing}`;
  };

  return (
    <StepLayout
      title={t('appSyncSelectProductTitle', 'Welches Konto richten wir ein?')}
      description={t(
        'appSyncSelectProductDescription',
        'Wir melden dich über die echte LinkedIn-App auf einem eigenen Gerät an. Das ist stabiler und braucht keine parallelen Browser-Sitzungen.',
      )}
      footer={
        <StepActions
          primaryText={t('next')}
          primaryDisabled={!selectedLicenseId}
          backText={onCancel ? t('cancel') : undefined}
          onBack={onCancel}
          onPrimary={onNext}
        />
      }
    >
      <Stack gap={1.5}>
        {linkedinProducts.map((product) => (
          <OptionCard
            key={product.value}
            title={product.label}
            subtitle={product.subtitle}
            icon={product.icon}
            iconColor={product.color}
            selected={selectedLinkedinProduct === product.value}
            onSelect={handleLinkedinProductChange(product.value)}
          />
        ))}
      </Stack>

      <Stack className={classes.licenseSection} gap={0.75}>
        <Typography className={classes.fieldLabel}>{t('appSyncLicenseSeat', 'Lizenz-Sitz')}</Typography>
        {fetchLoading ? (
          <Typography className={classes.helperText}>{t('loadingLicenses')}</Typography>
        ) : hasLicenses ? (
          <>
            <PaiSelect
              value={selectedLicenseId || ''}
              onChange={(event) => onLicenseSelect(event.target.value || null)}
            >
              <PaiMenuItem value={''} disabled={true}>
                {t('appSyncSelectLicenseSeat', 'Sitz auswählen')}
              </PaiMenuItem>
              {unlinkedLicenses.map((license) => (
                <PaiMenuItem key={license._id} value={license._id}>
                  {createLicenseLabel(license)}
                </PaiMenuItem>
              ))}
            </PaiSelect>
            <Typography className={classes.helperText}>
              {t('appSyncFreeSeats', '{{seats}} freie Sitze', { seats: unlinkedLicenses.length })}
            </Typography>
          </>
        ) : (
          <Alert severity={'warning'} icon={<WarningAmberIcon />} className={classes.alert}>
            <Typography className={classes.alertTitle}>{t('noLicensesAvailable')}</Typography>
            <Typography className={classes.helperText}>{t('noLicensesAvailableDescription')}</Typography>
          </Alert>
        )}
      </Stack>
    </StepLayout>
  );
};

const useStyles = makeStyles()((theme) => ({
  licenseSection: {
    marginTop: '22px',
  },
  fieldLabel: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color: theme.palette.greys.medium,
  },
  helperText: {
    fontSize: theme.typography.pxToRem(12.5),
    color: theme.palette.greys.medium,
    lineHeight: 1.5,
  },
  alert: {
    borderRadius: '10px',
  },
  alertTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    marginBottom: '4px',
  },
}));

export default SelectProductStep;
